import * as React from "react";

import type { Area, LatLng } from "@/types/area";

/**
 * Part hues, 45° apart round the wheel and none within 30° of red (0°): red is the "no area"
 * venue pin, grey an unparented area. Ordered so the first few, which greedy colouring hands out
 * most, sit far apart.
 */
export const PART_HUES: readonly number[] = [210, 30, 120, 300, 165, 75, 255];
const PART_SATURATION = 80;
const PART_LIGHTNESS = 45;

/**
 * Shades for the areas inside a part: a hue offset from the part's hue (at most ±20°, so a
 * shade stays nearer its own part's hue than any other part's, which are 45° away), and an
 * absolute saturation and lightness inside a safe band (lightness 32-70, never near-black or
 * near-white, and never clamped).
 *
 * Chosen by search so that, for every part hue, every pair of shades is at least 0.075 apart in
 * OKLab (about 4x the ~0.02 just-noticeable difference); `part-colours.test.ts` pins that.
 */
export const AREA_SHADES: readonly { dh: number; s: number; l: number }[] = [
  { dh: 5, s: 60, l: 32 },
  { dh: -15, s: 80, l: 38 },
  { dh: 20, s: 70, l: 50 },
  { dh: -10, s: 85, l: 58 },
  { dh: 20, s: 80, l: 66 },
  { dh: -15, s: 40, l: 70 },
];

/** Colour of unparented areas on the map (they belong to no part). */
export const UNPARENTED_COLOUR = "#6b7280";

/** Slack (in degrees, about 200 m) under which two parts' boxes count as touching. */
const PART_NEAR_DEG = 0.002;
/** How close (in degrees, about 30 m) two areas' borders must come to be neighbours. */
const AREA_NEAR_DEG = 0.0003;

const hsl = (h: number, s: number, l: number): string =>
  `hsl(${((h % 360) + 360) % 360}, ${s}%, ${l}%)`;

export const partColourAt = (hueIndex: number): string =>
  hsl(PART_HUES[hueIndex], PART_SATURATION, PART_LIGHTNESS);

export const areaShadeAt = (hueIndex: number, shadeIndex: number): string => {
  const { dh, s, l } = AREA_SHADES[shadeIndex];
  return hsl(PART_HUES[hueIndex] + dh, s, l);
};

interface Box {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

const boxOf = (shape: LatLng[] | null | undefined): Box | null => {
  if (!shape?.length) return null;
  const lats = shape.map((p) => p.latitude);
  const lngs = shape.map((p) => p.longitude);
  return {
    minLat: Math.min(...lats),
    maxLat: Math.max(...lats),
    minLng: Math.min(...lngs),
    maxLng: Math.max(...lngs),
  };
};

const boxesTouch = (a: Box, b: Box, slack: number): boolean =>
  a.minLat - slack <= b.maxLat &&
  b.minLat - slack <= a.maxLat &&
  a.minLng - slack <= b.maxLng &&
  b.minLng - slack <= a.maxLng;

type Pt = readonly [number, number];

const cross = (o: Pt, a: Pt, b: Pt): number =>
  (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

const pointSegmentDist = (p: Pt, a: Pt, b: Pt): number => {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = dx * dx + dy * dy;
  const t = len
    ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len))
    : 0;
  return Math.hypot(p[0] - a[0] - t * dx, p[1] - a[1] - t * dy);
};

/** Distance between two segments: 0 when they cross, else the nearest endpoint-to-segment gap. */
const segmentDist = (a: Pt, b: Pt, c: Pt, d: Pt): number => {
  const d1 = cross(c, d, a);
  const d2 = cross(c, d, b);
  const d3 = cross(a, b, c);
  const d4 = cross(a, b, d);
  if (d1 * d2 < 0 && d3 * d4 < 0) return 0;
  return Math.min(
    pointSegmentDist(a, c, d),
    pointSegmentDist(b, c, d),
    pointSegmentDist(c, a, b),
    pointSegmentDist(d, a, b),
  );
};

/** The shape's closed ring as [lat, lng·cos(lat)] edges, so a degree is about the same both ways. */
const edgesOf = (shape: LatLng[], cosLat: number): [Pt, Pt][] => {
  const pts: Pt[] = shape.map((p) => [p.latitude, p.longitude * cosLat]);
  return pts.map((p, i) => [p, pts[(i + 1) % pts.length]]);
};

const edgeInBox = ([a, b]: [Pt, Pt], box: Box, cosLat: number): boolean =>
  Math.max(a[0], b[0]) >= box.minLat - AREA_NEAR_DEG &&
  Math.min(a[0], b[0]) <= box.maxLat + AREA_NEAR_DEG &&
  Math.max(a[1], b[1]) >= box.minLng * cosLat - AREA_NEAR_DEG &&
  Math.min(a[1], b[1]) <= box.maxLng * cosLat + AREA_NEAR_DEG;

/**
 * Two areas are neighbours when their borders come within `AREA_NEAR_DEG`: a shared edge, a
 * crossing or a sliver gap, wherever the vertices fall. Only the edges inside the other shape's
 * box are compared.
 */
const bordersTouch = (a: Area, b: Area, boxA: Box, boxB: Box): boolean => {
  const cosLat = Math.cos((((boxA.minLat + boxA.maxLat) / 2) * Math.PI) / 180);
  const ea = edgesOf(a.shape, cosLat).filter((e) => edgeInBox(e, boxB, cosLat));
  const eb = edgesOf(b.shape, cosLat).filter((e) => edgeInBox(e, boxA, cosLat));
  return ea.some(([p, q]) =>
    eb.some(([r, s]) => segmentDist(p, q, r, s) <= AREA_NEAR_DEG),
  );
};

/** Neighbour sets for `items`, each pair tested once: boxes first, then `exact` if given. */
const neighbourSets = (
  items: readonly Area[],
  boxes: Map<string, Box | null>,
  slack: number,
  exact?: (a: Area, b: Area, boxA: Box, boxB: Box) => boolean,
): Map<string, Set<string>> => {
  const sets = new Map(items.map((a) => [a.id, new Set<string>()]));
  for (let i = 0; i < items.length; i++) {
    const a = items[i];
    const boxA = boxes.get(a.id);
    if (!boxA) continue;
    for (let j = i + 1; j < items.length; j++) {
      const b = items[j];
      const boxB = boxes.get(b.id);
      if (!boxB || !boxesTouch(boxA, boxB, slack)) continue;
      if (exact && !exact(a, b, boxA, boxB)) continue;
      sets.get(a.id)!.add(b.id);
      sets.get(b.id)!.add(a.id);
    }
  }
  return sets;
};

/**
 * Greedy graph colouring: in the given order, each node takes the first of `size` colours no
 * coloured neighbour has; when every colour is taken, the one fewest neighbours use.
 */
const greedyColour = (
  ids: string[],
  neighbours: Map<string, Set<string>>,
  size: number,
): Map<string, number> => {
  const out = new Map<string, number>();
  for (const id of ids) {
    const used = new Array<number>(size).fill(0);
    for (const other of neighbours.get(id) ?? []) {
      const c = out.get(other);
      if (c !== undefined) used[c]++;
    }
    const free = used.indexOf(0);
    out.set(id, free >= 0 ? free : used.indexOf(Math.min(...used)));
  }
  return out;
};

const byName = (a: Area, b: Area): number =>
  a.name.localeCompare(b.name) || a.id.localeCompare(b.id);

const byPartOrder = (a: Area, b: Area): number =>
  (a.partOrder ?? Number.POSITIVE_INFINITY) -
    (b.partOrder ?? Number.POSITIVE_INFINITY) || byName(a, b);

/**
 * A key that changes exactly when `areaColours` could: ids, names, kinds, parents, part order and
 * shapes. Memoise on it so a refetch that rebuilds the array does not recolour.
 */
export const areaColoursKey = (areas: readonly Area[]): string =>
  areas
    .map(
      (a) =>
        `${a.id}\u0001${a.name}\u0001${a.kind}\u0001${a.parentId ?? ""}\u0001${a.partOrder ?? ""}\u0001${a.shapeHash ?? JSON.stringify(a.shape ?? [])}`,
    )
    .sort()
    .join("\u0002");

/**
 * The colour of every part and area of a city, keyed by id: the one source for the map and the
 * tree legend beside it.
 *
 * - A part takes a hue by greedy colouring in `partOrder`: neighbouring parts (boxes overlapping
 *   or within ~200 m) never share a hue while the hues last.
 * - An area inside a part takes one of `AREA_SHADES` of that hue, by the same colouring over the
 *   part's areas (most-connected first): areas whose borders touch differ while the shades last.
 * - An area with no part, or whose part is not in `areas`, is `UNPARENTED_COLOUR`.
 *
 * A pure function of the set of areas: the input order does not matter.
 */
export const areaColours = (areas: readonly Area[]): Map<string, string> => {
  const parts = areas.filter((a) => a.kind === "part").sort(byPartOrder);
  const boxes = new Map(areas.map((a) => [a.id, boxOf(a.shape)]));

  const hueIndex = greedyColour(
    parts.map((p) => p.id),
    neighbourSets(parts, boxes, PART_NEAR_DEG),
    PART_HUES.length,
  );
  const colours = new Map<string, string>();
  for (const [id, i] of hueIndex) colours.set(id, partColourAt(i));

  for (const part of parts) {
    const children = areas.filter(
      (a) => a.kind !== "part" && a.parentId === part.id,
    );
    // Box slack doubled: the box test is in raw degrees, the border test in cos-scaled ones.
    const neighbours = neighbourSets(
      children,
      boxes,
      2 * AREA_NEAR_DEG,
      bordersTouch,
    );
    const ordered = [...children]
      .sort(
        (a, b) =>
          neighbours.get(b.id)!.size - neighbours.get(a.id)!.size ||
          byName(a, b),
      )
      .map((c) => c.id);
    const hue = hueIndex.get(part.id)!;
    for (const [id, i] of greedyColour(ordered, neighbours, AREA_SHADES.length))
      colours.set(id, areaShadeAt(hue, i));
  }
  for (const a of areas)
    if (!colours.has(a.id)) colours.set(a.id, UNPARENTED_COLOUR);
  return colours;
};

/**
 * `areaColours` memoised on `areaColoursKey`: computed once per distinct set of areas, not per
 * render or per refetch that rebuilds the array.
 */
export const useAreaColours = (areas: readonly Area[]): Map<string, string> => {
  const key = React.useMemo(() => areaColoursKey(areas), [areas]);
  const latest = React.useRef(areas);
  latest.current = areas;
  return React.useMemo(() => areaColours(latest.current), [key]);
};
